<template>
  <div class="booking-page">
    <HeaderComp></HeaderComp>

    <main class="booking-shell">
      <section class="booking-toolbar" aria-labelledby="booking-title">
        <div>
          <p class="booking-eyebrow">Töötajate kalender</p>
          <h1 id="booking-title">Broneeringud</h1>
        </div>
        <div class="toolbar-actions">
          <button type="button" class="secondary-button" @click="fetchBookings">
            Värskenda
          </button>
          <a class="logout-link" href="/.auth/logout?post_logout_redirect_uri=/">
            Logi välja
          </a>
        </div>
      </section>

      <section class="booking-layout">
        <form class="booking-form" @submit.prevent="saveBooking">
          <div class="panel-header">
            <h2>{{ editingId ? "Muuda broneeringut" : "Uus broneering" }}</h2>
            <button
              v-if="editingId"
              type="button"
              class="text-button"
              @click="resetForm"
            >
              Tühista
            </button>
          </div>

          <label>
            Klient
            <input v-model.trim="form.customerName" type="text" required />
          </label>

          <label>
            Telefon
            <input v-model.trim="form.phone" type="tel" required />
          </label>

          <label>
            Sõiduk / reg. nr
            <input v-model.trim="form.vehicle" type="text" />
          </label>

          <label>
            Teenus
            <select v-model="form.service" required>
              <option value="" disabled>Vali teenus</option>
              <option>Rehvide vahetus</option>
              <option>Balansseerimine</option>
              <option>Velgede remont</option>
              <option>Rehvide hoiustamine</option>
              <option>Muu töö</option>
            </select>
          </label>

          <div class="form-row">
            <label>
              Kuupäev
              <input v-model="form.date" type="date" required />
            </label>
            <label>
              Staatus
              <select v-model="form.status">
                <option value="booked">Broneeritud</option>
                <option value="confirmed">Kinnitatud</option>
                <option value="completed">Valmis</option>
              </select>
            </label>
          </div>

          <div class="form-row">
            <label>
              Algus
              <input v-model="form.startTime" type="time" required />
            </label>
            <label>
              Lõpp
              <input v-model="form.endTime" type="time" required />
            </label>
          </div>

          <label>
            Märkmed
            <textarea v-model.trim="form.notes" rows="4"></textarea>
          </label>

          <p v-if="formError" class="form-error">{{ formError }}</p>

          <button type="submit" class="primary-button" :disabled="saving">
            {{ saving ? "Salvestan..." : "Salvesta" }}
          </button>
        </form>

        <section class="schedule-panel" aria-label="Broneeringute kalender">
          <div class="schedule-controls">
            <label>
              Kuupäev
              <input v-model="selectedDate" type="date" @change="fetchBookings" />
            </label>
            <div class="view-toggle" aria-label="Kalendri vaade">
              <button
                type="button"
                :class="{ active: viewMode === 'day' }"
                @click="setViewMode('day')"
              >
                Päev
              </button>
              <button
                type="button"
                :class="{ active: viewMode === 'week' }"
                @click="setViewMode('week')"
              >
                Nädal
              </button>
            </div>
          </div>

          <p v-if="loading" class="schedule-state">Laen broneeringuid...</p>
          <p v-else-if="loadError" class="schedule-error">{{ loadError }}</p>

          <div v-else class="calendar-grid" :class="`calendar-${viewMode}`">
            <article
              v-for="day in visibleDays"
              :key="day.date"
              class="day-column"
            >
              <header class="day-header">
                <span>{{ day.label }}</span>
                <strong>{{ day.shortDate }}</strong>
              </header>

              <div v-if="day.bookings.length" class="booking-list">
                <button
                  v-for="booking in day.bookings"
                  :key="booking.id"
                  type="button"
                  class="booking-item"
                  :class="booking.status"
                  @click="editBooking(booking)"
                >
                  <span class="booking-time">
                    {{ booking.startTime }}-{{ booking.endTime }}
                  </span>
                  <span class="booking-name">{{ booking.customerName }}</span>
                  <span class="booking-meta">
                    {{ booking.service }}<template v-if="booking.vehicle">
                      · {{ booking.vehicle }}
                    </template>
                  </span>
                </button>
              </div>

              <p v-else class="empty-day">Vaba</p>
            </article>
          </div>

          <div v-if="editingId" class="danger-row">
            <button type="button" class="danger-button" @click="cancelBooking">
              Tühista broneering
            </button>
          </div>
        </section>
      </section>
    </main>
  </div>
</template>

<script>
import HeaderComp from "@/components/HeaderComp.vue";

const formatLocalDate = (date) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const today = () => formatLocalDate(new Date());

const defaultForm = () => ({
  customerName: "",
  phone: "",
  vehicle: "",
  service: "",
  date: today(),
  startTime: "09:00",
  endTime: "10:00",
  notes: "",
  status: "booked",
});

const toMinutes = (value) => {
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
};

const addDays = (date, days) => {
  const next = new Date(`${date}T00:00:00.000Z`);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
};

export default {
  name: "BookingView",
  components: {
    HeaderComp,
  },
  data() {
    return {
      bookings: [],
      selectedDate: today(),
      viewMode: "week",
      form: defaultForm(),
      editingId: "",
      editingOriginalDate: "",
      loading: false,
      saving: false,
      loadError: "",
      formError: "",
    };
  },
  computed: {
    range() {
      if (this.viewMode === "day") {
        return { from: this.selectedDate, to: this.selectedDate };
      }

      const date = new Date(`${this.selectedDate}T12:00:00`);
      const day = date.getDay() || 7;
      const monday = addDays(this.selectedDate, 1 - day);
      return { from: monday, to: addDays(monday, 6) };
    },
    visibleDays() {
      const days = [];
      const dayCount = this.viewMode === "day" ? 1 : 7;

      for (let index = 0; index < dayCount; index += 1) {
        const date = addDays(this.range.from, index);
        const dateObject = new Date(`${date}T12:00:00`);
        const bookings = this.bookings
          .filter((booking) => booking.date === date)
          .sort((first, second) =>
            first.startTime.localeCompare(second.startTime)
          );

        days.push({
          date,
          label: dateObject.toLocaleDateString("et-EE", { weekday: "long" }),
          shortDate: dateObject.toLocaleDateString("et-EE", {
            day: "2-digit",
            month: "2-digit",
          }),
          bookings,
        });
      }

      return days;
    },
  },
  mounted() {
    this.fetchBookings();
  },
  methods: {
    redirectToLogin() {
      window.location.href =
        "/.auth/login/aad?post_login_redirect_uri=/booking";
    },
    async parseJsonResponse(response) {
      if (
        response.redirected ||
        response.url.includes("/.auth/login") ||
        response.url.includes("login.microsoftonline.com")
      ) {
        this.redirectToLogin();
        return null;
      }

      const body = await response.text();
      const contentType = response.headers.get("content-type") || "";

      if (!body) {
        if (response.status === 401 || response.status === 403) {
          this.redirectToLogin();
          return null;
        }

        return {};
      }

      if (!contentType.includes("application/json")) {
        throw new Error("Broneeringu API ei tagastanud JSON vastust.");
      }

      try {
        return JSON.parse(body);
      } catch (error) {
        throw new Error("Broneeringu API tagastas vigase JSON vastuse.");
      }
    },
    setViewMode(mode) {
      this.viewMode = mode;
      this.fetchBookings();
    },
    async fetchBookings() {
      this.loading = true;
      this.loadError = "";

      try {
        const response = await fetch(
          `/api/bookings?from=${this.range.from}&to=${this.range.to}`,
          { credentials: "include" }
        );

        const payload = await this.parseJsonResponse(response);
        if (!payload) return;

        if (!response.ok) {
          throw new Error(payload.error || "Broneeringuid ei saanud laadida.");
        }

        this.bookings = payload.bookings || [];
      } catch (error) {
        this.loadError = error.message;
      } finally {
        this.loading = false;
      }
    },
    validateForm() {
      if (toMinutes(this.form.endTime) <= toMinutes(this.form.startTime)) {
        return "Lõppaeg peab olema hilisem kui algusaeg.";
      }

      return "";
    },
    bookingPayload() {
      return {
        customerName: this.form.customerName,
        phone: this.form.phone,
        vehicle: this.form.vehicle,
        service: this.form.service,
        date: this.form.date,
        startTime: this.form.startTime,
        endTime: this.form.endTime,
        notes: this.form.notes,
        status: this.form.status,
        originalDate: this.editingOriginalDate,
      };
    },
    async saveBooking() {
      this.formError = this.validateForm();
      if (this.formError) return;

      this.saving = true;

      try {
        const endpoint = this.editingId
          ? `/api/bookings/${this.editingId}?date=${this.editingOriginalDate}`
          : "/api/bookings";
        const response = await fetch(endpoint, {
          method: this.editingId ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(this.bookingPayload()),
        });
        const payload = await this.parseJsonResponse(response);
        if (!payload) return;

        if (!response.ok) {
          throw new Error(payload.error || "Broneeringut ei saanud salvestada.");
        }

        this.selectedDate = this.form.date;
        this.resetForm();
        await this.fetchBookings();
      } catch (error) {
        this.formError = error.message;
      } finally {
        this.saving = false;
      }
    },
    editBooking(booking) {
      this.editingId = booking.id;
      this.editingOriginalDate = booking.date;
      this.form = {
        customerName: booking.customerName || "",
        phone: booking.phone || "",
        vehicle: booking.vehicle || "",
        service: booking.service || "",
        date: booking.date,
        startTime: booking.startTime,
        endTime: booking.endTime,
        notes: booking.notes || "",
        status: booking.status || "booked",
      };
      this.formError = "";
    },
    async cancelBooking() {
      if (!this.editingId) return;
      const confirmed = window.confirm("Kas tühistada see broneering?");
      if (!confirmed) return;

      try {
        const response = await fetch(
          `/api/bookings/${this.editingId}?date=${this.editingOriginalDate}`,
          { method: "DELETE", credentials: "include" }
        );
        const payload = await this.parseJsonResponse(response);
        if (!payload) return;

        if (!response.ok) {
          throw new Error(payload.error || "Broneeringut ei saanud tühistada.");
        }

        this.resetForm();
        await this.fetchBookings();
      } catch (error) {
        this.formError = error.message;
      }
    },
    resetForm() {
      this.form = defaultForm();
      this.editingId = "";
      this.editingOriginalDate = "";
      this.formError = "";
    },
  },
};
</script>

<style scoped>
.booking-page {
  min-height: 100vh;
  background: #f7f4f6;
  color: #211a20;
}

.booking-shell {
  width: min(100% - 32px, 1180px);
  margin: 0 auto;
  padding: 32px 0 56px 0;
}

.booking-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.booking-eyebrow {
  margin: 0 0 6px 0;
  color: #8a1d55;
  font-weight: 800;
  text-transform: uppercase;
  font-size: 0.78rem;
  letter-spacing: 0;
}

h1,
h2 {
  margin: 0;
  line-height: 1.1;
}

h1 {
  font-size: 2.2rem;
}

h2 {
  font-size: 1.05rem;
}

.toolbar-actions,
.panel-header,
.schedule-controls,
.form-row,
.danger-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.booking-layout {
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.booking-form,
.schedule-panel {
  background: #ffffff;
  border: 1px solid #ead7e1;
  border-radius: 8px;
  box-shadow: 0 18px 42px rgba(42, 22, 34, 0.08);
}

.booking-form {
  display: flex;
  flex-direction: column;
  gap: 13px;
  padding: 18px;
}

.panel-header {
  justify-content: space-between;
}

label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  color: #3c3139;
  font-size: 0.9rem;
  font-weight: 700;
}

input,
select,
textarea {
  width: 100%;
  min-width: 0;
  border: 1px solid #cdb9c4;
  border-radius: 7px;
  background: #ffffff;
  color: #211a20;
  font: inherit;
  padding: 10px 11px;
  box-sizing: border-box;
}

textarea {
  resize: vertical;
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #e22289;
  box-shadow: 0 0 0 3px rgba(226, 34, 137, 0.14);
}

.form-row {
  align-items: stretch;
}

.form-row label {
  flex: 1 1 0;
}

.primary-button,
.secondary-button,
.logout-link,
.text-button,
.danger-button,
.view-toggle button {
  min-height: 40px;
  border-radius: 7px;
  border: 1px solid transparent;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
}

.primary-button {
  background: #e22289;
  color: #ffffff;
}

.primary-button:disabled {
  opacity: 0.64;
  cursor: wait;
}

.secondary-button,
.logout-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  color: #211a20;
  border-color: #d8bdcc;
  padding: 0 14px;
}

.logout-link {
  background: #211a20;
  color: #ffffff;
}

.text-button {
  background: transparent;
  color: #8a1d55;
  padding: 0;
}

.danger-button {
  background: #fff2f2;
  color: #9d1f1f;
  border-color: #efb5b5;
  padding: 0 14px;
}

.schedule-panel {
  min-width: 0;
  padding: 16px;
}

.schedule-controls {
  justify-content: space-between;
  margin-bottom: 14px;
}

.schedule-controls label {
  width: min(100%, 220px);
}

.view-toggle {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid #d8bdcc;
  border-radius: 8px;
  background: #f7f4f6;
}

.view-toggle button {
  background: transparent;
  color: #463842;
  padding: 0 14px;
}

.view-toggle button.active {
  background: #211a20;
  color: #ffffff;
}

.calendar-grid {
  display: grid;
  gap: 10px;
}

.calendar-week {
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.calendar-day {
  grid-template-columns: 1fr;
}

.day-column {
  min-width: 0;
  min-height: 280px;
  border: 1px solid #ead7e1;
  border-radius: 8px;
  background: #fbf8fa;
  overflow: hidden;
}

.day-header {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px;
  background: #211a20;
  color: #ffffff;
}

.day-header span {
  text-transform: capitalize;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.78);
}

.booking-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 10px;
}

.booking-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 84px;
  padding: 10px;
  text-align: left;
  border: 1px solid #e7c8d8;
  border-radius: 7px;
  background: #ffffff;
  color: #211a20;
  cursor: pointer;
  overflow: hidden;
  white-space: normal;
}

.booking-item.confirmed {
  border-color: #82b69b;
}

.booking-item.completed {
  opacity: 0.72;
}

.booking-time {
  color: #8a1d55;
  font-weight: 900;
}

.booking-name {
  font-weight: 800;
}

.booking-meta {
  color: #66565f;
  font-size: 0.86rem;
  line-height: 1.35;
}

.booking-time,
.booking-name,
.booking-meta {
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.calendar-week .booking-list {
  padding: 8px;
}

.calendar-week .booking-item {
  gap: 5px;
  padding: 9px;
}

.calendar-week .booking-time {
  font-size: 0.88rem;
  line-height: 1.2;
}

.calendar-week .booking-name {
  font-size: 0.9rem;
  line-height: 1.25;
}

.calendar-week .booking-meta {
  font-size: 0.78rem;
}

.empty-day,
.schedule-state,
.schedule-error,
.form-error {
  margin: 0;
}

.empty-day {
  padding: 14px 10px;
  color: #7d6c76;
}

.schedule-state {
  color: #66565f;
}

.schedule-error,
.form-error {
  color: #9d1f1f;
  font-weight: 700;
}

.danger-row {
  justify-content: flex-end;
  margin-top: 14px;
}

@media (max-width: 960px) {
  .booking-layout {
    grid-template-columns: 1fr;
  }

  .calendar-week {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .booking-shell {
    width: calc(100% - 20px);
    padding-top: 20px;
  }

  .booking-toolbar,
  .schedule-controls,
  .form-row {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-actions {
    justify-content: stretch;
  }

  .secondary-button,
  .logout-link {
    width: 100%;
  }

  .calendar-week {
    grid-template-columns: 1fr;
  }
}
</style>
